import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { projects } from '../data/projects';

function FadeIn({ children, delay = 0 }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current; if (!el) return;
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
        }, { threshold: 0.06 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return <div ref={ref} className="fade-in" style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

function StatusBadge({ status }) {
    const map = {
        'Completed': { dot: '#22C55E', label: 'Completed' },
        'In Progress': { dot: 'var(--accent)', label: 'In Progress' },
        'Interrupted': { dot: '#6B7280', label: 'Interrupted' }
    };
    const { dot, label } = map[status] || { dot: '#6B7280', label: status };
    return (
        <span className="status-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />
            {label}
        </span>
    );
}

function ProjectDetail() {
    const { projectId } = useParams();
    const project = projects.find((p) => p.id === projectId);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);

    if (!project) {
        return <div className="page"><div className="container" style={{ textAlign: 'center', paddingTop: 80 }}>Project not found</div></div>;
    }

    const parseMarkdown = (text) => {
        if (!text) return null;
        const parts = [];
        let lastIndex = 0;
        const regex = /\*\*(.*?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
        let match;
        let key = 0;
        while ((match = regex.exec(text)) !== null) {
            if (match.index > lastIndex) parts.push(text.substring(lastIndex, match.index));
            if (match[1]) {
                parts.push(<strong style={{ color: 'var(--text)' }} key={key++}>{match[1]}</strong>);
            } else if (match[2] && match[3]) {
                parts.push(<a key={key++} href={match[3]} target="_blank" rel="noopener noreferrer" className="project-link">{match[2]}</a>);
            }
            lastIndex = regex.lastIndex;
        }
        if (lastIndex < text.length) parts.push(text.substring(lastIndex));
        return parts.length > 0 ? parts : text;
    };

    const handleNextImage = (e) => { e.stopPropagation(); if (selectedImageIndex < project.gallery.length - 1) setSelectedImageIndex(selectedImageIndex + 1); };
    const handlePrevImage = (e) => { e.stopPropagation(); if (selectedImageIndex > 0) setSelectedImageIndex(selectedImageIndex - 1); };

    const BASE = 'https://www.salvatore.losardo.eu';
    const canonicalUrl = `${BASE}/projects/${project.id}`;
    const ogImage = project.gallery?.[0]
        ? `${BASE}${project.gallery[0]}`
        : `${BASE}/og-image.jpg`;

    const creativeWorkSchema = {
        '@context': 'https://schema.org',
        '@type': 'CreativeWork',
        name: project.name || project.title,
        description: project.description,
        url: canonicalUrl,
        image: ogImage,
        dateCreated: project.startDate,
        dateModified: project.endDate || project.startDate,
        author: { '@type': 'Person', name: 'Salvatore Lo Sardo', url: BASE },
        keywords: [...(project.languages || []), ...(project.categories || [])].join(', '),
    };

    return (
        <>
        <Helmet>
            <title>{project.name || project.title} | Salvatore Lo Sardo</title>
            <meta name="description" content={project.description} />
            <meta property="og:title" content={`${project.name || project.title} | Salvatore Lo Sardo`} />
            <meta property="og:description" content={project.description} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={ogImage} />
            <meta name="twitter:image" content={ogImage} />
            <link rel="canonical" href={canonicalUrl} />
            <script type="application/ld+json">{JSON.stringify(creativeWorkSchema)}</script>
        </Helmet>
        <div className="page">
            <div className="container">
                <FadeIn>
                    <Link to="/projects" className="project-link" style={{ marginBottom: 24, display: 'inline-block' }}>&larr; Back to Projects</Link>

                    <div className="project-card" style={{ padding: '32px 40px', cursor: 'auto' }}>
                        <div className="project-card-top" style={{ marginBottom: 20 }}>
                            <StatusBadge status={project.state || project.status} />
                            {project.availability && <span className="project-visibility" style={{ marginLeft: 12 }}>{project.availability}</span>}
                            <span className="project-date" style={{ marginLeft: 'auto' }}>
                                {project.startDate || 'Unknown'} - {project.endDate || 'Present'}
                            </span>
                        </div>

                        <h1 className="page-hero-title" style={{ fontSize: 32, marginBottom: 24 }}>{project.name || project.title}</h1>

                        <p className="project-desc" style={{ WebkitLineClamp: 'unset', marginBottom: 32, whiteSpace: 'pre-line', fontSize: 16 }}>
                            {parseMarkdown(project.longDescription || project.description)}
                        </p>

                        <div className="tag-row" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                            <span style={{ color: 'var(--text-dim)', fontSize: 13, marginRight: 8, alignSelf: 'center' }}>Technologies:</span>
                            {(project.languages || []).map((lang) => <span key={lang} className="tag">{lang}</span>)}
                        </div>

                        <div className="tag-row" style={{ marginBottom: 24, flexWrap: 'wrap' }}>
                            <span style={{ color: 'var(--text-dim)', fontSize: 13, marginRight: 8, alignSelf: 'center' }}>Categories:</span>
                            {(project.categories || []).map((cat) => <span key={cat} className="tag tag--dim">{cat}</span>)}
                        </div>

                        {project.link && project.availability?.toLowerCase() === 'public' && (
                            <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
                                <a href={project.link} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ display: 'inline-flex' }}>
                                    View Repository ↗
                                </a>
                            </div>
                        )}
                    </div>
                </FadeIn>

                {project.gallery && project.gallery.length > 0 && (
                    <FadeIn delay={100}>
                        <div style={{ marginTop: 40 }}>
                            <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text)', marginBottom: 20 }}>Gallery</h2>
                            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                                {project.gallery.slice(0, 1).map((img, index) => (
                                    <div key={index} style={{ position: 'relative', cursor: 'pointer', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }} onClick={() => setSelectedImageIndex(index)}>
                                        <img src={img} alt={`Screenshot ${index + 1}`} style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'cover' }} />
                                        {index === 0 && project.gallery.length > 1 && (
                                            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span style={{ color: '#fff', fontSize: 20, fontWeight: 'bold' }}>+{project.gallery.length - 1} images</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FadeIn>
                )}
            </div>

            {/* Lightbox */}
            {selectedImageIndex !== null && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.95)' }} onClick={() => setSelectedImageIndex(null)}>
                    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <button style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '50%', width: 48, height: 48, fontSize: 24, cursor: 'pointer', zIndex: 10 }} onClick={() => setSelectedImageIndex(null)}>×</button>

                        {selectedImageIndex > 0 && (
                            <button style={{ position: 'absolute', left: 20, background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '50%', width: 56, height: 56, fontSize: 32, cursor: 'pointer', zIndex: 10 }} onClick={handlePrevImage}>‹</button>
                        )}

                        <img src={project.gallery[selectedImageIndex]} alt={`Gallery ${selectedImageIndex + 1}`} onClick={e => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} />

                        {selectedImageIndex < project.gallery.length - 1 && (
                            <button style={{ position: 'absolute', right: 20, background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '50%', width: 56, height: 56, fontSize: 32, cursor: 'pointer', zIndex: 10 }} onClick={handleNextImage}>›</button>
                        )}

                        <div style={{ position: 'absolute', bottom: 24, color: 'rgba(255,255,255,0.7)', fontSize: 16 }}>
                            {selectedImageIndex + 1} / {project.gallery.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}

export default ProjectDetail;
